import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Phone, Video, MessageSquare, Smartphone, Monitor, AppWindow, ArrowLeftRight, Sparkles } from 'lucide-react';
import * as Icons from 'lucide-react';
import PageHero from '../components/PageHero';
import CtaBanner from '../components/CtaBanner';
import FaqAccordion from '../components/FaqAccordion';
import Seo from '../components/Seo';
import { UC_PILLARS, NEW_PAGE_IMAGES } from '../data/mock';

const FAQ = [
  { q: 'What is Unified Communications (UC)?', a: 'UC brings every workplace conversation — calls, video, chat, SMS and presence — into a single platform with a shared identity and contact directory.' },
  { q: 'Do my CRM activities sync automatically?', a: 'Yes. Calls, messages and meetings are auto-logged in your CRM with notes, durations and outcomes. We support 250+ CRMs natively.' },
  { q: 'Can I message external contacts?', a: 'Yes. Internal team chat plus inbound/outbound SMS to any phone number, all from the same window.' },
  { q: 'Is my data secure?', a: 'All voice, video and messaging is encrypted in transit (TLS/SRTP). Optional E2EE and ISO 27001-aligned hosting are available.' }
];

export default function UnifiedComms() {
  return (
    <>
      <Seo
        title="Unified Communications (UCaaS) | VoIPinger"
        description="One identity for voice, video, chat, SMS and presence. Mobile, desktop, web and SIP desk phone — same softphone identity across every device with seamless handover."
        path="/unified-communications"
        image="https://voipinger.com/assets/heroes/unified-communications.png"
        service={{ name: 'Unified Communications as a Service', serviceType: 'UCaaS' }}
        faq={FAQ}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Unified Communications', path: '/unified-communications' }]}
      />
      <PageHero
        eyebrow="Unified Communications"
        title="One identity. Every conversation."
        subtitle="Combine voice, video, chat, SMS, presence and CRM workflow into a single experience — on desktop, mobile or browser."
        breadcrumb="Unified Communications"
      />

      <section className="bg-white">
        <div className="container-x section-pad grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -inset-5 rounded-3xl bg-gradient-to-br from-violet-100 via-purple-100 to-fuchsia-100 -z-10" />
            <img src={NEW_PAGE_IMAGES.uc} alt="Unified Communications platform" className="rounded-2xl shadow-xl w-full" loading="lazy" />
          </div>
          <div className="order-1 lg:order-2">
            <span className="eyebrow">UCaaS made simple</span>
            <h2 className="section-title mt-4">Tear down the silos</h2>
            <p className="section-sub mt-4">
              Stop hopping between Zoom, Slack and your phone. VoIPinger UC unifies every channel under one number, one calendar and one search bar — with the analytics to back it up.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-2">
              {['Single identity', 'Cross-device handoff', 'Presence everywhere', 'CRM auto-logging', 'AI summaries', 'Single bill'].map((b) => (
                <li key={b} className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-violet-600" /><span className="text-sm">{b}</span></li>
              ))}
            </ul>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="btn-primary">Try UC free <ArrowRight size={16} /></Link>
              <Link to="/phone-features" className="btn-light">See all features</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-100">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow">UC pillars</span>
            <h2 className="section-title mt-4">Six capabilities, one platform</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {UC_PILLARS.map((f) => {
              const Ic = Icons[f.icon] || Icons.Sparkles;
              return (
                <div key={f.t} className="card-modern">
                  <div className="w-11 h-11 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center mb-3"><Ic size={20} /></div>
                  <h3 className="font-bold text-slate-900 mb-1">{f.t}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* CHANNELS — one identity across voice / video / chat / SMS        */}
      {/* ============================================================== */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow">Every channel, one stack</span>
            <h2 className="section-title mt-4">One platform — five ways to reach anyone</h2>
            <p className="section-sub mt-3">
              The same identity carries across every channel. Switch from a chat to a call to a video
              meeting to an SMS — without changing tab.
            </p>
            <div className="divider-svg mt-6" />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {[
              { i: Phone, t: 'Voice', d: 'HD PSTN calling, click-to-dial, full call control across desk, mobile and web.' },
              { i: Video, t: 'Video', d: 'WebRTC meetings, conference rooms and webinars up to 1,000 attendees.' },
              { i: MessageSquare, t: 'Team chat', d: '1:1 and group messaging with file share and threaded replies.' },
              { i: Smartphone, t: 'SMS', d: 'Inbound + outbound SMS to any mobile, from the same contact card.' },
              { i: Sparkles, t: 'Presence', d: 'See who is on a call, available, in a meeting or offline — live across every device.' }
            ].map((c) => (
              <div key={c.t} className="rounded-2xl bg-white border border-slate-200 p-5 hover:border-violet-300 hover:shadow-md transition-all">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center mb-3 shadow-md shadow-violet-500/20">
                  <c.i size={18} />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">{c.t}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* DEVICES — desktop / mobile / web / desk phone                    */}
      {/* ============================================================== */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow">Any device</span>
            <h2 className="section-title mt-4">Pick up where you left off, on any screen</h2>
            <p className="section-sub mt-3">
              Switching devices mid-call is a one-tap action — no transfer, no dropped audio, no re-dial.
              Same softphone identity across mobile, desktop, browser and your SIP desk phone.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
            {[
              { i: Smartphone, t: 'Mobile app', d: 'iOS + Android softphone with visual voicemail, presence and seamless WiFi / cellular handoff.' },
              { i: Monitor, t: 'Desktop app', d: 'Windows + macOS native softphone with click-to-dial, presence and call pop.' },
              { i: AppWindow, t: 'Web phone', d: 'Browser-based WebRTC softphone — no install. Google / Microsoft 365 SSO.' },
              { i: Phone, t: 'SIP desk phone', d: '250+ supported devices from 21+ vendors — Polycom, Yealink, Cisco, Snom, etc.' }
            ].map((c) => (
              <div key={c.t} className="rounded-2xl bg-white border border-slate-200 p-5 hover:border-violet-300 hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center mb-4 shadow-md shadow-violet-500/20">
                  <c.i size={22} />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{c.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>

          <div className="max-w-4xl mx-auto mt-10 rounded-2xl border border-violet-100 bg-violet-50/50 p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/30">
                <ArrowLeftRight size={22} />
              </div>
              <div>
                <h3 className="font-extrabold text-slate-900 text-lg mb-2">Call handover, mid-conversation</h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  On a desk-phone call and need to leave the office? Tap "Switch device" in the mobile
                  app — the call moves to your phone in under a second, the caller hears nothing.
                  Same trick from mobile back to desktop, or to the web app.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FaqAccordion items={FAQ} title="Unified Communications FAQ" />
      <CtaBanner title="Unify your team's communications" subtitle="Same number, same identity, every device. Try VoIPinger UC for 14 days." />
    </>
  );
}
